import React, { useState, useEffect, useRef } from 'react';
import { Search, Stethoscope, Calendar, MapPin, Phone, Mail, DollarSign, Clock, User, FileText, GraduationCap, Award, SlidersHorizontal, X, Eye, CalendarCheck, Check, Bookmark, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDoctorsLibrary, getDoctorById } from '../../redux/doctorSlice';
import { getallspecilitylist, getSpCategoryList, getCountries } from '../../redux/masterSlice';
import { getPatientById } from '../../redux/patientSlice';
import CommonLoader from '../../components/CommonLoader';
import Pagination from '../../components/Pagination';
import DoctorDetailsModal from '../../components/modals/DoctorDetailsModal';
import BookAppointmentModal from '../../components/modals/BookAppointmentModal';
import errorimg from '../../assets/img/colorkit (1).svg';
import { getFileURL } from '../../utils/fileURL';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterPopover } from '../../components/common/FilterPanel';
import MasterLabel from '../../components/common/MasterLabel';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export default function DoctorLibrary() {
  const { t } = useTranslation('doctorlibrary');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isInitialCall = useRef(true);
  const isPatientDataFetched = useRef(false);
  const { doctorsLibrary, doctorsLibraryLoading, doctorsLibraryTotal, doctorsLibraryPage, doctorsLibraryLimit, doctorsLibraryTotalPages, error } = useSelector((state) => state.doctor);
  const { specialties, specialtyLoading, spCategoryList, spCategoryListLoading, countries } = useSelector((state) => state.master);
  const { selectedPatient, isLoading: patientLoading } = useSelector((state) => state.patient);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const defaultFilters = { gender: [], countryId: [], category: '', specialties: [] };
  const [tempFilters, setTempFilters] = useState({ ...defaultFilters });
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [isDoctorDetailsModalOpen, setIsDoctorDetailsModalOpen] = useState(false);
  const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] = useState(false);
  const [selectedDoctorForDetails, setSelectedDoctorForDetails] = useState(null);
  const [selectedDoctorForBooking, setSelectedDoctorForBooking] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [tempSpecialtySearch, setTempSpecialtySearch] = useState('');
  const [tempCategorySearch, setTempCategorySearch] = useState('');
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(true);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const filterButtonRef = useRef(null);
  const filterRef = useRef(null);
  const patientId = localStorage.getItem('ID') || '';
  const userRole = localStorage.getItem('Role') || '';

  const debouncedTempSpecialtySearch = useDebounce(tempSpecialtySearch, 400);

  // Maximum length for search inputs
  const MAX_SEARCH_LENGTH = 100;

  // Find applied category object for display
  const appliedCategoryObj = (spCategoryList || []).find(c => c.spcategoryId === filters.category);

  // Fetch countries on mount
  useEffect(() => {
    dispatch(getCountries({ status: 'active', limit: 100 }));
  }, [dispatch]);

  // Fetch patient data to check profile status
  useEffect(() => {
    if (userRole === 'patient' && patientId && !isPatientDataFetched.current) {
      dispatch(getPatientById(patientId));
      isPatientDataFetched.current = true;
    }
  }, [dispatch, patientId, userRole]);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only debounce if there's no error
    if (!searchError) {
      searchTimeoutRef.current = setTimeout(() => {
        // Trim the search term before setting it
        const trimmedSearch = searchTerm.trim();
        setDebouncedSearchTerm(trimmedSearch);
        setCurrentPage(1);
      }, 500);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchError]);

  // Fetch category list on mount
  useEffect(() => {
    dispatch(getSpCategoryList());
    // Also pre-load the master specialty list so doctor cards can resolve a
    // specialtyId from the specialty name (used by <MasterLabel> as a fallback
    // when the API doesn't include specialtyId on the doctor.specialty objects).
    dispatch(getallspecilitylist({}));
  }, [dispatch]);

  // Fetch specialty list when temp category changes (for filter panel)
  useEffect(() => {
    if (tempFilters.category) {
      const trimmedSearch = debouncedTempSpecialtySearch.trim();
      dispatch(getallspecilitylist({ search: trimmedSearch, categoryId: tempFilters.category }));
    }
  }, [dispatch, tempFilters.category, debouncedTempSpecialtySearch]);

  // Sync tempFilters with filters when popup opens
  useEffect(() => {
    if (isFilterOpen) {
      setTempFilters({ ...filters });
      setTempSpecialtySearch('');
      setTempCategorySearch('');
      setIsCategoryDrawerOpen(true);
      if (filters.category) {
        dispatch(getallspecilitylist({ categoryId: filters.category }));
      }
    }
  }, [isFilterOpen]);

  // Close filter on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setTempFilters({ ...filters });
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filters]);

  // Refocus search input after loading
  useEffect(() => {
    const active = document.activeElement === inputRef.current;
    if (!active && inputRef.current && searchTerm.length > 0) {
      inputRef.current.focus();
    }
  }, [doctorsLibraryLoading, searchTerm]);

  // Fetch doctors library
  useEffect(() => {
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm.trim(),
      patientId: patientId,
      specialty: filters.specialties.join(','),
      categoryId: filters.category
    };
    if (filters.gender.length > 0) params.gender = filters.gender.join(',');
    if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');

    if (isInitialCall.current) {
      dispatch(getDoctorsLibrary(params));
      isInitialCall.current = false;
      return;
    }

    if (!searchError) {
      dispatch(getDoctorsLibrary(params));
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filters, dispatch, searchError]);

  const handlePageChange = (page, newItemsPerPage) => {
    if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  const validateSearchInput = (value) => {
    // Remove any potentially dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check length
    if (sanitized.length > MAX_SEARCH_LENGTH) {
      setSearchError(t('doctorlibrary.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`));
      return false;
    }

    // Check for only whitespace
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      setSearchError(t('doctorlibrary.search_only_spaces', 'Search term cannot contain only spaces'));
      return false;
    }

    setSearchError('');
    return true;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;

    // Sanitize input - remove dangerous characters
    const sanitized = value.replace(/[<>]/g, '');
    const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

    // Always update searchTerm so the input stays in sync
    setSearchTerm(finalValue);

    // Validate input
    validateSearchInput(finalValue);
  };

  // Format doctors with fullName
  const formattedDoctors = (doctorsLibrary || []).map((doctor) => ({
    ...doctor,
    fullName: `${doctor.name || ''} ${doctor.surname || ''}`.trim(),
    id: doctor.doctorId || doctor._id,
    onViewClick: (doc) => {
      setSelectedDoctorForDetails(doc);
      setIsDoctorDetailsModalOpen(true);
      dispatch(getDoctorById(doc.doctorId || doc.id));
    },
    onBookClick: (doc) => {
      setSelectedDoctorForBooking(doc);
      setIsBookAppointmentModalOpen(true);
      dispatch(getDoctorById(doc.doctorId || doc.id));
    }
  }));

  const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);

  // Check if patient profile is incomplete - restrict viewing/booking doctors
  if (userRole === 'patient' && selectedPatient && selectedPatient.profileStatus === 'incomplete' && !patientLoading) {
    return (
      <div data-testid="doctor-library-profile-incomplete-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
        <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
          <img
            data-testid="doctor-library-profile-incomplete-icon"
            src={errorimg}
            alt={t('doctorlibrary.profile_incomplete_alt', 'Profile Incomplete')}
            className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain"
          />
          <h2 data-testid="doctor-library-profile-incomplete-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">
            {t('doctorlibrary.unable_to_access', 'Unable to Access Doctor Library')}
          </h2>
          <p data-testid="doctor-library-profile-incomplete-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
            {t('doctorlibrary.profile_incomplete_message', 'Your profile is incomplete. Please complete your profile to view and book appointments with doctors.')}
          </p>
          <p data-testid="doctor-library-update-profile-message" className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-6 md:mb-8">
            {t('doctorlibrary.update_profile_message', 'Please update your profile information to continue.')}
          </p>
          <button
            data-testid="doctor-library-go-to-profile-button"
            onClick={() => navigate('/patientProfile')}
            className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 cursor-pointer bg-[#4ABDBD] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:bg-[#3aa8a8] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50"
          >
            {t('doctorlibrary.go_to_profile', 'Go to Profile')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="doctor-library-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
      {/* Header */}
      <div data-testid="doctor-library-header-section" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 data-testid="doctor-library-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
          {t('doctorlibrary.title')}
        </h1>

        {/* Search, Specialty Select and Filter */}
        <div data-testid="doctor-library-search-filter-section" className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-80">
            <input
              data-testid="doctor-library-search-input"
              ref={inputRef}
              type="text"
              placeholder={t('doctorlibrary.search_placeholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={doctorsLibraryLoading}
              maxLength={MAX_SEARCH_LENGTH}
              className={`w-full p-2 pl-10 pr-8 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${searchError
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300'
                } ${doctorsLibraryLoading ? "opacity-70" : ""}`}
            />
            <Search data-testid="doctor-library-search-icon" className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#4ABDBD]" />
            {searchError && (
              <p data-testid="doctor-library-search-error" className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0">{searchError}</p>
            )}
            {searchTerm.length > 0 && !searchError && (
              <p data-testid="doctor-library-search-counter" className="text-gray-400 text-xs mt-1 absolute -bottom-5 left-0">
                {searchTerm.length}/{MAX_SEARCH_LENGTH} {t('doctorlibrary.characters', 'characters')}
              </p>
            )}
          </div>

          {/* Filter Button + Popup */}
          <div className="relative w-full sm:w-auto">
            <button
              data-testid="doctor-library-filter-button"
              ref={filterButtonRef}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="group flex items-center justify-center gap-2 h-full px-3 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer w-full sm:w-auto"
            >
              <SlidersHorizontal data-testid="doctor-library-filter-icon" className="w-5 h-5" />
            </button>

            {isFilterOpen && (
              <FilterPopover ref={filterRef}>
                <FilterPanel
                    onClear={() => {
                      setTempFilters({ ...defaultFilters });
                      setFilters({ ...defaultFilters });
                      setTempSpecialtySearch('');
                      setTempCategorySearch('');
                      setCurrentPage(1);
                      setIsFilterOpen(false);
                    }}
                    onApply={() => {
                      setFilters({ ...tempFilters });
                      setCurrentPage(1);
                      setIsFilterOpen(false);
                    }}
                    clearLabel={t('doctorlibrary.clear')}
                    applyLabel={t('doctorlibrary.apply')}
                >
                    {/* Specialty */}
                    <FilterSection title={t('doctorlibrary.specialty_label', 'Specialty')} isActive={!!tempFilters.category || tempFilters.specialties.length > 0}>
                        <div className="flex flex-col -mx-3 -mt-3 border-t border-gray-100">

                            {/* Heading + search */}
                            <div className="px-3 pt-3 pb-2">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                    <input
                                        data-testid="doctor-library-category-search-input"
                                        type="text"
                                        value={tempCategorySearch}
                                        onChange={(e) => setTempCategorySearch(e.target.value)}
                                        placeholder={t('doctorlibrary.search_category', 'Search category...')}
                                        className="w-full pl-7 pr-7 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                    />
                                    {tempCategorySearch && (
                                        <button data-testid="doctor-library-category-search-clear-button" type="button" onClick={() => setTempCategorySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
                                            <X className="w-3 h-3 text-gray-400" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Category list — left-tab style with drawer */}
                            <div className="flex flex-col">
                                {spCategoryListLoading ? (
                                    <div className="py-4 flex justify-center">
                                        <span className="h-4 w-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                    </div>
                                ) : (spCategoryList || []).filter(cat => cat.spcategoryName?.toLowerCase().includes(tempCategorySearch.toLowerCase())).map(cat => {
                                    const isCatSelected = tempFilters.category === cat.spcategoryId;
                                    return (
                                        <div data-testid={`doctor-library-category-row-${cat.spcategoryId}`} key={cat.spcategoryId}>
                                            {/* Category row — left nav tab style */}
                                            <button
                                                data-testid={`doctor-library-category-button-${cat.spcategoryId}`}
                                                type="button"
                                                onClick={() => {
                                                    if (isCatSelected) {
                                                        setTempFilters({ ...tempFilters, category: '', specialties: [] });
                                                        setTempSpecialtySearch('');
                                                    } else {
                                                        setTempFilters({ ...tempFilters, category: cat.spcategoryId, specialties: [] });
                                                        setTempSpecialtySearch('');
                                                        setTempCategorySearch('');
                                                        dispatch(getallspecilitylist({ categoryId: cat.spcategoryId }));
                                                    }
                                                }}
                                                className={`w-full flex items-center justify-between px-3 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer border-t border-gray-100 ${
                                                    isCatSelected
                                                        ? 'bg-white text-[#4ABDBD] shadow-sm'
                                                        : 'text-gray-700 hover:bg-white/60 hover:text-[#4ABDBD]'
                                                }`}
                                            >
                                                <span className="flex items-center gap-1.5 text-xs truncate">
                                                    {isCatSelected && <span className="w-1.5 h-1.5 rounded-full bg-[#4ABDBD] shrink-0" />}
                                                    <MasterLabel type="masterSpecialtyCategory" id={cat.spcategoryId} field="spcategoryName" fallback={cat.spcategoryName} />
                                                </span>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    {isCatSelected && tempFilters.specialties.length > 0 && (
                                                        <span className="px-1.5 py-0.5 rounded-full bg-[#4ABDBD] text-white text-[10px] font-semibold">{tempFilters.specialties.length}</span>
                                                    )}
                                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCatSelected ? 'rotate-180 text-[#4ABDBD]' : 'text-gray-400'}`} />
                                                </div>
                                            </button>

                                            {/* Drawer — specialty list for this category */}
                                            {isCatSelected && (
                                                <div className="bg-gray-50/50 border-t border-gray-100 px-3 py-2">
                                                    {specialties && specialties.length > 5 && (
                                                        <div className="relative mb-2">
                                                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                                            <input
                                                                data-testid="doctor-library-specialty-search-input"
                                                                type="text"
                                                                value={tempSpecialtySearch}
                                                                onChange={(e) => setTempSpecialtySearch(e.target.value)}
                                                                placeholder={t('doctorlibrary.search_specialty', 'Search specialty...')}
                                                                className="w-full pl-7 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                                            />
                                                            {tempSpecialtySearch && (
                                                                <button data-testid="doctor-library-specialty-search-clear-button" type="button" onClick={() => setTempSpecialtySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
                                                                    <X className="w-3 h-3 text-gray-400" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    {specialtyLoading ? (
                                                        <div className="py-4 flex justify-center">
                                                            <span className="h-4 w-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                        </div>
                                                    ) : (!specialties || specialties.length === 0) ? (
                                                        <div className="py-3 text-center text-xs text-gray-400">
                                                            {t('doctorlibrary.no_specialties', 'No specialties found')}
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-0.5 max-h-[180px] overflow-y-auto custom-scrollbar">
                                                            {specialties.map((sp) => {
                                                                const spId = sp.specialtyId || sp.id;
                                                                const isSelected = tempFilters.specialties.includes(spId);
                                                                return (
                                                                    <button
                                                                        data-testid={`doctor-library-specialty-option-${spId}`}
                                                                        key={spId}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const updated = isSelected
                                                                                ? tempFilters.specialties.filter(id => id !== spId)
                                                                                : [...tempFilters.specialties, spId];
                                                                            setTempFilters({ ...tempFilters, specialties: updated });
                                                                        }}
                                                                        className={`w-full flex items-center gap-2 px-2 py-2 text-xs rounded-lg transition-all duration-150 cursor-pointer ${
                                                                            isSelected ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold' : 'text-gray-700 hover:bg-white'
                                                                        }`}
                                                                    >
                                                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all duration-150 ${
                                                                            isSelected ? 'border-[#4ABDBD] bg-[#4ABDBD]' : 'border-gray-300'
                                                                        }`}>
                                                                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                                                                        </div>
                                                                        <span className="truncate text-left">
                                                                            <MasterLabel
                                                                                type="masterSpecialty"
                                                                                id={spId}
                                                                                field="name"
                                                                                fallback={sp.name || '-'}
                                                                            />
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    </FilterSection>

                    {/* Gender */}
                    <FilterSection title={t('doctorlibrary.gender_label')} isActive={tempFilters.gender.length > 0}>
                        <FilterOptionList
                            multiple
                            value={tempFilters.gender}
                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                            options={[
                                { value: 'male', label: t('doctorlibrary.gender.male') },
                                { value: 'female', label: t('doctorlibrary.gender.female') },
                                { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                                { value: 'other', label: t('doctorlibrary.gender.other') },
                            ]}
                        />
                    </FilterSection>

                    {/* Country */}
                    <FilterSection title={t('doctorlibrary.country_label')} isActive={tempFilters.countryId.length > 0}>
                        <FilterCountryList
                            multiple
                            value={tempFilters.countryId}
                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                            countries={countries}
                        />
                    </FilterSection>
                </FilterPanel>
              </FilterPopover>
            )}
          </div>
        </div>
      </div>

      {/* Applied Filters Display */}
      {(filters.category || filters.specialties.length > 0 || filters.gender.length > 0 || filters.countryId.length > 0) && (
        <div data-testid="doctor-library-applied-filters-section" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="doctor-library-applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
              {t('doctorlibrary.applied_filters', 'Applied Filters:')}
            </span>

            {appliedCategoryObj && (
              <div data-testid="doctor-library-applied-category-filter" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span data-testid="doctor-library-applied-category-text" className="text-xs text-gray-600">
                  <span className="font-semibold">{t('doctorlibrary.category_label', 'Category')}:</span> <MasterLabel type="masterSpecialtyCategory" id={appliedCategoryObj.spcategoryId} field="spcategoryName" fallback={appliedCategoryObj.spcategoryName} />
                </span>
                <button
                  data-testid="doctor-library-clear-category-filter-button"
                  onClick={() => {
                    setFilters({ ...filters, category: '', specialties: [] });
                    setCurrentPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {filters.specialties.length > 0 && (
              <div data-testid="doctor-library-applied-specialty-filter" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span data-testid="doctor-library-applied-specialty-text" className="text-xs text-gray-600">
                  <span className="font-semibold">{t('doctorlibrary.specialty_label', 'Specialty')}:</span> {filters.specialties.length} {t('doctorlibrary.selected', 'selected')}
                </span>
                <button
                  data-testid="doctor-library-clear-specialty-filter-button"
                  onClick={() => {
                    setFilters({ ...filters, specialties: [] });
                    setCurrentPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {filters.gender.map((g) => (
              <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span className="text-xs text-gray-600">
                  <span className="font-semibold">{t('doctorlibrary.gender_label', 'Gender')}:</span> {t(`common.gender.${g.toLowerCase()}`, g)}
                </span>
                <button
                  onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            {filters.countryId.map((cid) => (
              <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                <span className="text-xs text-gray-600">
                  <span className="font-semibold">{t('doctorlibrary.country_label', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                </span>
                <button
                  onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}

            <button
              data-testid="doctor-library-clear-all-filters-button"
              onClick={() => {
                setFilters({ ...defaultFilters });
                setTempFilters({ ...defaultFilters });
                setCurrentPage(1);
              }}
              className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
              {t('doctorlibrary.clear_all', 'Clear All')}
            </button>
          </div>
        </div>
      )}

      {/* Doctor Cards Grid */}
      {doctorsLibraryLoading ? (
        <div data-testid="doctor-library-loading-container" className="flex flex-col items-center justify-center py-12">
          <CommonLoader />
          <p data-testid="doctor-library-loading-text" className="text-gray-500 text-lg mt-4">{t('doctorlibrary.loading_doctors', 'Loading doctors...')}</p>
        </div>
      ) : formattedDoctors.length === 0 ? (
        <div data-testid="doctor-library-empty-container" className="text-center py-12">
          <p data-testid="doctor-library-empty-text" className="text-gray-500 text-lg">{t('doctorlibrary.no_doctors_found_criteria', 'No doctors found matching your search criteria.')}</p>
        </div>
      ) : (
        <>
          <div data-testid="doctor-library-cards-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {formattedDoctors.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
          {!doctorsLibraryLoading && doctorsLibraryTotal > 0 && (
            <div data-testid="doctor-library-pagination-container" className="mt-6">
              <Pagination
                totalItems={doctorsLibraryTotal}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                custombutton={formattedDoctors.length === 0 ? 'disable' : ''}
              />
            </div>
          )}
        </>
      )}

      {/* Doctor Details Modal */}
      <DoctorDetailsModal
        isOpen={isDoctorDetailsModalOpen}
        onClose={() => {
          setIsDoctorDetailsModalOpen(false);
          setSelectedDoctorForDetails(null);
        }}
        doctor={selectedDoctor}
        loading={doctorLoading}
      />

      {/* Book Appointment Modal */}
      <BookAppointmentModal
        isOpen={isBookAppointmentModalOpen}
        onClose={() => {
          setIsBookAppointmentModalOpen(false);
          setSelectedDoctorForBooking(null);
        }}
        doctor={selectedDoctor}
        loading={doctorLoading}
        onAppointmentBooked={() => {
          // Close both modals
          setIsBookAppointmentModalOpen(false);
          setSelectedDoctorForBooking(null);
          // Refresh doctor library data
          const refreshParams = {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchTerm,
            patientId: patientId,
            specialty: filters.specialties.join(','),
            categoryId: filters.category
          };
          if (filters.gender.length > 0) refreshParams.gender = filters.gender.join(',');
          if (filters.countryId.length > 0) refreshParams.countryId = filters.countryId.join(',');
          dispatch(getDoctorsLibrary(refreshParams));
        }}
      />
    </div>
  );
}

function DoctorCard({ doctor }) {
  const { t } = useTranslation('doctorlibrary');
  const [isSpecialtiesExpanded, setIsSpecialtiesExpanded] = useState(false);
  // Master specialty list — used as a name-based fallback to resolve specialtyId
  // when the API doesn't include it on the doctor's specialty objects directly.
  const masterSpecialtiesList = useSelector((state) => state.master.specialties);
  const imageUrl = getFileURL(doctor.image || doctor.photo) || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.fullName || doctor.name || 'Doctor')}&size=150&background=4ABDBD&color=fff&bold=true`;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return 'DR';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(doctor.fullName || doctor.name);

  // Get certification title (0th index) or default to "General Doctor"
  const getCertificationTitle = () => {
    if (doctor.certifiactions && Array.isArray(doctor.certifiactions) && doctor.certifiactions.length > 0) {
      return doctor.certifiactions[0].title || t('doctorlibrary.general_doctor', 'General Doctor');
    }
    return t('doctorlibrary.general_doctor', 'General Doctor');
  };

  const certificationTitle = getCertificationTitle();

  // Days of week mapping
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const daysOfWeekTranslated = [
    t('doctorlibrary.days.monday', 'Monday'),
    t('doctorlibrary.days.tuesday', 'Tuesday'),
    t('doctorlibrary.days.wednesday', 'Wednesday'),
    t('doctorlibrary.days.thursday', 'Thursday'),
    t('doctorlibrary.days.friday', 'Friday'),
    t('doctorlibrary.days.saturday', 'Saturday'),
    t('doctorlibrary.days.sunday', 'Sunday'),
  ];
  const dayAbbreviations = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  // Get availability status for a specific day
  const getDayAvailabilityStatus = (dayName) => {
    if (!doctor.availability || !Array.isArray(doctor.availability)) {
      return null; // No availability data
    }
    const dayAvailability = doctor.availability.find(avail => avail.day === dayName);
    if (!dayAvailability) {
      return false; // Day not found means not available
    }
    // Check status field if it exists, otherwise default to true if day exists
    return dayAvailability.status !== undefined ? dayAvailability.status : true;
  };

  // Check if all availability objects have isActive === false
  const areAllAvailabilityInactive = () => {
    if (!doctor.availability || !Array.isArray(doctor.availability) || !doctor.availability.length === 0) {
      return true; // No availability data means hide button
    }
    // Check if all objects have isActive === false
    return doctor.availability.every(avail => avail.status === false);
  };

  return (
    <div data-testid={`doctor-library-card-${doctor.id}`} className="group relative flex flex-col justify-between bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 hover:shadow-2xl hover:border-[#4ABDBD]/60 transform hover:-translate-y-2">
      {/* Modern Teal Header with Avatar */}
      <div>
        <div className="relative p-5 overflow-hidden bg-gradient-to-br from-[#4ABDBD] via-[#3AA8A8] to-[#2D8F8F]">
          {/* Enhanced Decorative Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}></div>
          </div>

          {/* Gradient Shine Effect */}
          <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/10 to-transparent"></div>

          <div className="relative flex items-start gap-4">
            {/* Large Avatar with Enhanced Gradient Design */}
            <div className="flex-shrink-0 relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-sm border-2 border-white/40 shadow-xl overflow-hidden flex items-center justify-center relative ring-2 ring-white/20">
                {/* Gradient Border Glow */}
                <div className="absolute -inset-0.5 rounded-2xl opacity-30 blur-sm bg-gradient-to-br from-[#4ABDBD] via-[#3AA8A8] to-[#2D8F8F]"></div>
                {imageUrl && !imageUrl.includes('ui-avatars') ? (
                  <>
                    <img
                      src={getFileURL(imageUrl)}
                      alt={doctor.fullName || doctor.name}
                      className="w-full h-full object-cover relative z-10"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="avatar-fallback hidden w-full h-full absolute inset-0 items-center justify-center text-white font-bold text-2xl relative z-10 bg-gradient-to-br from-[#4ABDBD]/40 to-[#3AA8A8]/40">
                      {initials}
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl relative z-10 bg-gradient-to-br from-[#4ABDBD]/40 to-[#3AA8A8]/40">
                    {initials}
                  </div>
                )}
              </div>
            </div>

            {/* Doctor Name and Info */}
            <div className="flex-1 min-w-0 pt-1">
              <h3 data-testid={`doctor-library-card-name-${doctor.id}`} className="font-bold text-xl text-white mb-3 truncate drop-shadow-lg">
                {doctor.fullName || doctor.name || '-'}
              </h3>

              {/* Gender, Certification Title, and License No */}
              <div className="flex items-start gap-2 flex-wrap">
                {/* Gender and Certification Title */}
                {/* <div className="flex-1 min-w-0 space-y-2"> */}
                {doctor.gender && (
                  <div data-testid={`doctor-library-card-gender-${doctor.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <User className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold capitalize">
                      {t(`common.gender.${doctor.gender.toLowerCase()}`, doctor.gender)}
                    </span>
                  </div>
                )}

                {/* Profession badge — shows the doctor's first profession with a
                    "+N" overflow chip when there are more. Hovering the chip reveals
                    the remaining professions in a tooltip. Falls back to the
                    certification title when the doctor has no profession set. */}
                {Array.isArray(doctor.professions) && doctor.professions.length > 0 ? (
                  <div data-testid={`doctor-library-card-professions-${doctor.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <GraduationCap className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      <MasterLabel
                        type="masterProfession"
                        id={doctor.professions[0].professionId}
                        field="professionName"
                        fallback={doctor.professions[0].professionName}
                      />
                    </span>
                    {doctor.professions.length > 1 && (
                      <div className="relative group">
                        <span className="text-white text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full cursor-default">
                          +{doctor.professions.length - 1}
                        </span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap flex flex-col gap-1">
                            {doctor.professions.slice(1).map((p) => (
                              <span key={p.professionId}>
                                <MasterLabel
                                  type="masterProfession"
                                  id={p.professionId}
                                  field="professionName"
                                  fallback={p.professionName}
                                />
                              </span>
                            ))}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : doctor.profession?.professionName ? (
                  <div data-testid={`doctor-library-card-profession-${doctor.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <GraduationCap className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      <MasterLabel
                        type="masterProfession"
                        id={doctor.profession.professionId}
                        field="professionName"
                        fallback={doctor.profession.professionName}
                      />
                    </span>
                  </div>
                ) : (
                  <div data-testid={`doctor-library-card-certification-${doctor.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <GraduationCap className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      {certificationTitle}
                    </span>
                  </div>
                )}

                {/* License No */}
                {doctor.licenseNumber && (
                  <div data-testid={`doctor-library-card-license-${doctor.id}`} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/20">
                    <FileText className="w-4 h-4 text-white flex-shrink-0" />
                    <span className="text-white text-xs font-semibold truncate">
                      {doctor.licenseNumber}
                    </span>
                  </div>
                )}
              </div>
              {/* </div> */}
            </div>
          </div>
        </div>

        {/* Clean White Body Section */}
        <div className="px-6 py-4 bg-white">

          {/* Contact Information */}
          <div className="space-y-3">
            {doctor.phone && (
              <div className='flex items-center justify-between'>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                  <span data-testid={`doctor-library-card-phone-${doctor.id}`} className="text-sm text-gray-700">{doctor.phone}</span>
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2 ">
                    <button
                      data-testid={`doctor-library-view-button-${doctor.id}`}
                      onClick={() => doctor.onViewClick && doctor.onViewClick(doctor)}
                      className=" flex items-center justify-center gap-1.5 bg-[#4ABDBD]/10 backdrop-blur-sm p-2 rounded-xl border border-[#4ABDBD]/20 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium text-xs"
                    >
                      <Eye className="w-3.5 h-3.5 text-[#4ABDBD]" />
                      {/* View Details */}
                    </button>
                    {!areAllAvailabilityInactive() && (
                      <button
                        data-testid={`doctor-library-book-button-${doctor.id}`}
                        onClick={() => doctor.onBookClick && doctor.onBookClick(doctor)}
                        className=" flex items-center justify-center gap-1.5 p-2 px-4 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium text-xs"
                      >
                        {/* <CalendarCheck className="w-3.5 h-3.5" /> */}
                        {t('doctorlibrary.book', 'Book')}
                      </button>
                    )}
                  </div>
              </div>
            )}
            {doctor.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
                <span data-testid={`doctor-library-card-email-${doctor.id}`} className="text-sm text-gray-700 truncate">{doctor.email}</span>
              </div>
            )}

            {/* Availability Days */}
            <div data-testid={`doctor-library-card-availability-section-${doctor.id}`} className="flex items-center gap-3">
              <Calendar className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
              <div className="flex-1 flex items-center gap-1.5">
                {dayAbbreviations.map((abbr, index) => {
                  const dayName = daysOfWeek[index];
                  const dayStatus = getDayAvailabilityStatus(dayName);
                  const isAvailable = dayStatus === true;
                  const isUnavailable = dayStatus === false;

                  return (
                    <div data-testid={`doctor-library-card-day-${doctor.id}-${index}`} key={index} className="relative group/day flex items-center justify-center">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all cursor-default ${isAvailable
                          ? 'bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white shadow-md'
                          : isUnavailable
                            ? 'bg-red-100 text-red-600 border border-red-300'
                            : 'bg-gray-100 text-gray-400'
                          }`}
                      >
                        {abbr}
                      </div>
                      {/* Custom Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-lg text-white text-[10px] font-semibold whitespace-nowrap pointer-events-none opacity-0 group-hover/day:opacity-100 transition-opacity duration-200 shadow-lg z-50"
                        style={{
                          backgroundColor: isAvailable ? '#3AA8A8' : isUnavailable ? '#ef4444' : '#6b7280'
                        }}
                      >
                        {daysOfWeekTranslated[index]}
                        <span className="ml-1 opacity-80">
                          {isAvailable ? `· ${t('doctorlibrary.available')}` : isUnavailable ? `· ${t('doctorlibrary.not_available')}` : ''}
                        </span>
                        {/* Arrow */}
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                          style={{
                            borderTopColor: isAvailable ? '#3AA8A8' : isUnavailable ? '#ef4444' : '#6b7280'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Specialties Section - Right after availability days */}
            <div data-testid={`doctor-library-card-specialties-section-${doctor.id}`} className="flex items-center gap-3">
              <Award className="w-4 h-4 flex-shrink-0 text-[#4ABDBD]" />
              <div className="flex-1">
                {Array.isArray(doctor.specialty) && doctor.specialty.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {isSpecialtiesExpanded ? (
                      // Show all specialties when expanded
                      doctor.specialty.map((spec, index) => {
                        const specialtyColor = spec.color || '#4ABDBD';
                        const specName = spec.name || spec;
                        // If the API didn't include specialtyId on the doctor object,
                        // fall back to looking it up in the master specialties list by name.
                        const resolvedId = spec.specialtyId
                          || masterSpecialtiesList?.find((s) => s.name === specName)?.specialtyId
                          || null;
                        return (
                          <span
                            data-testid={`doctor-library-specialty-tag-${doctor.id}-${index}`}
                            key={index}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-white shadow-sm"
                            style={{
                              backgroundColor: specialtyColor,
                              borderColor: specialtyColor
                            }}
                          >
                            <MasterLabel type="masterSpecialty" id={resolvedId} field="name" fallback={specName} />
                          </span>
                        );
                      })
                    ) : (
                      // Show only first 2 specialties when collapsed
                      <>
                        {doctor.specialty.slice(0, 2).map((spec, index) => {
                          const specialtyColor = spec.color || '#4ABDBD';
                          const specName = spec.name || spec;
                          const resolvedId = spec.specialtyId
                            || masterSpecialtiesList?.find((s) => s.name === specName)?.specialtyId
                            || null;
                          return (
                            <span
                              data-testid={`doctor-library-specialty-collapsed-tag-${doctor.id}-${index}`}
                              key={index}
                              className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-white shadow-sm"
                              style={{
                                backgroundColor: specialtyColor,
                                borderColor: specialtyColor
                              }}
                            >
                              <MasterLabel type="masterSpecialty" id={resolvedId} field="name" fallback={specName} />
                            </span>
                          );
                        })}
                        {doctor.specialty.length > 2 && (
                          <button
                            data-testid={`doctor-library-specialty-expand-button-${doctor.id}`}
                            onClick={() => setIsSpecialtiesExpanded(true)}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-all duration-200 cursor-pointer text-[#4ABDBD] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 hover:bg-[#4ABDBD]/20"
                          >
                            +{doctor.specialty.length - 2}
                          </button>
                        )}
                      </>
                    )}
                    {isSpecialtiesExpanded && doctor.specialty.length > 2 && (
                      <button
                        data-testid={`doctor-library-specialty-collapse-button-${doctor.id}`}
                        onClick={() => setIsSpecialtiesExpanded(false)}
                        className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-all duration-200 cursor-pointer ${doctor.alreadyMy ? 'text-gray-500 bg-gray-100 border border-gray-300 hover:bg-gray-200' : 'text-[#4ABDBD] bg-[#4ABDBD]/10 border border-[#4ABDBD]/30 hover:bg-[#4ABDBD]/20'}`}
                      >
                        -
                      </button>
                    )}
                  </div>
                ) : (
                  <span data-testid={`doctor-library-card-no-specialty-${doctor.id}`} className="text-sm text-gray-500 italic">
                    {t('doctorlibrary.no_specialty', 'There is no specialty')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle Bottom Accent */}
      <div className="h-1 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#4ABDBD]"></div>

      {/* Save Icon - Bottom Right Corner */}
      {doctor.alreadyMy && (
        <div data-testid={`doctor-library-bookmark-container-${doctor.id}`} className="absolute bottom-4 right-4 z-10 group/save">
          <Bookmark data-testid={`doctor-library-bookmark-icon-${doctor.id}`} className="w-8 h-8 text-[#ec4899] fill-[#ec4899] drop-shadow-lg transition-all duration-200" />
          {/* Tooltip */}
          <div data-testid={`doctor-library-bookmark-tooltip-${doctor.id}`} className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/save:opacity-100 transition-opacity duration-200 pointer-events-none shadow-lg">
            {t('doctorlibrary.already_in_my_doctor', 'already in my doctor')}
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
}

